import React, { useState, useEffect } from 'react';
import { Photo, PhotoLabels, PhotoType } from '../../../types/annotation';
import PhotoCarousel from '../photo/PhotoCarousel';
import PhotoLabelForm from '../photo/PhotoLabelForm';
import { ImageOff } from 'lucide-react';

interface PhotoAnnotationPanelProps {
  photos: Photo[];
  isReviewMode: boolean;
  onUpdateLabels: (photoId: string, labels: Partial<PhotoLabels>, photoType?: PhotoType | null) => void;
  onSupervisorReview?: (photoId: string, verified: boolean, notes: string) => void;
  horizontal?: boolean;
}

const PhotoAnnotationPanel: React.FC<PhotoAnnotationPanelProps> = ({
  photos,
  isReviewMode,
  onUpdateLabels,
  onSupervisorReview,
  horizontal = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const safeIndex = Math.min(currentIndex, Math.max(0, photos.length - 1));
  const currentPhoto = photos[safeIndex] ?? null;

  // Local state per photo — ensures full merged labels are always sent to the API,
  // preventing partial updates from wiping previously-saved fields.
  const [localLabels, setLocalLabels] = useState<Partial<PhotoLabels>>(currentPhoto?.labels ?? {});
  const [localPhotoType, setLocalPhotoType] = useState<PhotoType | null>(
    currentPhoto?.photoType ?? null,
  );

  // Sync when navigating to a different photo
  useEffect(() => {
    setLocalLabels(currentPhoto?.labels ?? {});
    setLocalPhotoType(currentPhoto?.photoType ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhoto?.photoId]);

  if (photos.length === 0) {
    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <ImageOff className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-600">No photos in this session</p>
          <p className="text-xs text-gray-400 mt-1">
            Photos attached to the session will appear here.
          </p>
        </div>
      </div>
    );
  }

  const handleLabelsChange = (updates: Partial<PhotoLabels>) => {
    const merged = { ...localLabels, ...updates };
    setLocalLabels(merged);
    onUpdateLabels(currentPhoto!.photoId, merged);
  };

  const handlePhotoTypeChange = (type: PhotoType | null) => {
    setLocalPhotoType(type);
    // Pass full current labels alongside photoType so the backend doesn't wipe labels
    onUpdateLabels(currentPhoto!.photoId, { ...localLabels }, type);
  };

  const handleSupervisorVerify = (verified: boolean, notes: string) => {
    onSupervisorReview?.(currentPhoto!.photoId, verified, notes);
  };

  const labelForm = (
    <PhotoLabelForm
      photoType={localPhotoType}
      labels={localLabels}
      onChange={handleLabelsChange}
      onPhotoTypeChange={handlePhotoTypeChange}
      disabled={isReviewMode}
      onSupervisorVerify={
        isReviewMode && onSupervisorReview ? handleSupervisorVerify : undefined
      }
    />
  );

  if (horizontal) {
    return (
      <div className="flex h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Left: photo viewer */}
        <div className="flex-1 min-w-0 bg-gray-900 flex flex-col items-center justify-center p-6 overflow-y-auto">
          <div className="w-full max-w-3xl">
            <PhotoCarousel
              photos={photos}
              currentIndex={safeIndex}
              onIndexChange={setCurrentIndex}
              fillHeight
            />
          </div>
        </div>

        {/* Right: label form */}
        <div className="w-80 xl:w-96 flex-shrink-0 border-l border-gray-200 overflow-y-auto">
          <div className="px-4 py-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Labels — Photo {safeIndex + 1}
            </p>
            {labelForm}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-700">
          Photos
          <span className="ml-2 text-xs font-normal text-gray-400">
            {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </span>
        </h3>
      </div>

      {/* Content: stacked layout, carousel on top, form below */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 pb-2">
          <PhotoCarousel
            photos={photos}
            currentIndex={safeIndex}
            onIndexChange={setCurrentIndex}
          />
        </div>
        <div className="border-t border-gray-100 mx-4" />
        <div className="px-4 py-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Labels — Photo {safeIndex + 1}
          </p>
          {labelForm}
        </div>
      </div>
    </div>
  );
};

export default PhotoAnnotationPanel;
